import { useNavigate } from '@tanstack/react-router';
import { isNil } from 'lodash';
import { useCallback, useEffect } from 'react';
import { RETURN_CHANNEL_SLUG_QUERY } from '@shared/scenes/cards/form/update/constants';
import { i18n } from '@shared/util/i18n';
import { RouteName } from './routeParams';
import {
  Location,
  useNavigationBeforeLeave,
  useNavigationFocus,
  useNavigator,
} from './useNavigator';

export function useUnsavedChangesNavigationWarning(
  enabled: boolean,
  /**
   * Navigations to allow without asking, on top of the ones this hook already allows. For a form
   * whose own modals are child routes: those navigations change the URL without leaving the form, so
   * there is nothing to lose and nothing to warn about.
   */
  isAllowed?: (nextLocation: Location) => boolean,
) {
  const focused = useNavigationFocus();
  const callback = useCallback(
    (nextLocation: Location): boolean | Promise<boolean> => {
      const isSubformRedirect = !isNil(nextLocation?.params?.[RETURN_CHANNEL_SLUG_QUERY]);

      if (isSubformRedirect || !enabled || !focused || isAllowed?.(nextLocation) === true) {
        return true;
      }

      // return false to prevent navigation. can't use MBAlert here because this
      // callback needs to be synchronous

      // eslint-disable-next-line no-alert
      return window.confirm(i18n('unsavedChangesWarning.web.message'));
    },
    [enabled, focused, isAllowed],
  );

  useNavigationBeforeLeave(callback);

  // Add beforeunload handler to catch browser navigation (window.location, tab close, refresh, etc.)
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Modern browsers ignore the custom message and show their own
      event.preventDefault();
      // Chrome requires returnValue to be set
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled]);
}

export function useNavigateToRoute(routeName: RouteName): VoidFunction {
  const { navigate } = useNavigator();
  return useCallback(() => navigate(routeName), [navigate, routeName]);
}

type BuilderRoute =
  | '/field'
  | '/subform'
  | '/automation'
  | '/template/i'
  | '/template/e'
  | '/template/c';

interface BuilderLinkProps {
  route: BuilderRoute;
}

const BUILDER_ROUTES: Record<BuilderRoute, string> = {
  '/field': '/$channelSlug/builder/$workflowTemplateId/field',
  '/subform': '/$channelSlug/builder/$workflowTemplateId/subform',
  '/automation': '/$channelSlug/builder/$workflowTemplateId/automation',
  '/template/i': '/$channelSlug/builder/$workflowTemplateId/template/i',
  '/template/e': '/$channelSlug/builder/$workflowTemplateId/template/e',
  '/template/c': '/$channelSlug/builder/$workflowTemplateId/template/c',
};

/**
 * Navigates to a builder area for a workflow template. The builder lives in this app under
 * `/$channelSlug/builder/$workflowTemplateId/*`, so both ids are required; without them the
 * call logs and does nothing.
 */
export function useBuilderLink(workflowTemplateId?: string, channelSlug?: string) {
  const navigate = useNavigate();

  return useCallback(
    ({ route }: BuilderLinkProps) => {
      if (!workflowTemplateId || !channelSlug) {
        console.error('useBuilderLink: workflowTemplateId and channelSlug are required');
        return;
      }

      navigate({ to: BUILDER_ROUTES[route], params: { channelSlug, workflowTemplateId } });
    },
    [workflowTemplateId, channelSlug, navigate],
  );
}
